import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { categoryCounts, pickItems, sortInventory } from '@kronos/domain'
import type { InventorySort } from '@kronos/domain'
import { repository } from '../../data'
import { Icon } from '../icons'
import { navigate } from '../router'
import { EmptyState, PageHeader } from '../primitives'
import { Link } from '../shell/Link'
import { CategoriesDialog } from './CategoriesDialog'
import { CategoryChips } from './CategoryChips'
import { InventoryTable } from './InventoryTable'
import { InventoryToolbar, useInventoryLayout } from './InventoryToolbar'
import { Glyph } from './glyphs'
import { ItemGrid } from './ItemTile'
import { ItemEditor } from './ItemEditor'
import { SummaryStrip } from './SummaryStrip'
import { useCan } from './shared'

/**
 * Inventory — every item and how many we have, picture first.
 *
 * A gallery of tiles by default (photo, name, the number on the shelf, a bar
 * against its minimum); a compact list for the person scanning by name, and
 * the choice remembered. The strip on top says what needs attention.
 *
 * **The one place items are managed.** Engineers get Add item and Categories
 * at the top right — Add item opening `ItemEditor`, the
 * same form an item's own Edit button opens — because this is where someone
 * notices an item is missing. Everyone else sees the shelf read-only, and
 * intake gets Receive; the server refuses their item writes regardless.
 */
export function InventoryView(): JSX.Element {
  const items = useQuery({ queryKey: ['inventory'], queryFn: () => repository.inventory() })
  const can = useCan()
  const [query, setQuery] = useState('')
  const [category, setCategory] = useState<string | null>(null)
  const [sort, setSort] = useState<InventorySort>('name')
  const [layout, setLayout] = useInventoryLayout()
  const [dialog, setDialog] = useState<'new' | 'categories' | null>(null)

  const all = items.data ?? []
  // While typing, `pickItems` ranks by how well the name matches; otherwise
  // the chosen order applies.
  const matched = pickItems(all, query, category)
  const shown = query ? matched : sortInventory(matched, sort)
  const editor = can('edit_catalog')

  return (
    <div className="page inv-page">
      <PageHeader
        title="Inventory"
        subtitle={items.data ? `${all.length} item${all.length === 1 ? '' : 's'} · tap one to see every unit and delivery` : undefined}
        actions={
          <>
            {editor ? (
              <button type="button" onClick={() => setDialog('categories')}>
                Categories
              </button>
            ) : null}
            {editor ? (
              <button type="button" className={can('receive_stock') ? '' : 'primary'} onClick={() => setDialog('new')}>
                <Glyph name="plus" />
                Add item
              </button>
            ) : null}
            {can('receive_stock') ? (
              <Link to={{ name: 'receive', item: null }} className="button primary">
                <Icon name="receive" />
                Receive
              </Link>
            ) : null}
          </>
        }
      />

      {all.length > 0 ? <SummaryStrip /> : null}

      {all.length > 0 ? (
        <div className="inv-filters">
          <InventoryToolbar query={query} onQuery={setQuery} sort={sort} onSort={setSort} layout={layout} onLayout={setLayout} />
          <CategoryChips counts={categoryCounts(all)} value={category} onChange={setCategory} />
        </div>
      ) : null}

      {items.isError ? <EmptyState icon="stock" title="Could not load the inventory" /> : null}
      {items.data && all.length === 0 ? (
        <EmptyState
          icon="stock"
          title="No items yet"
          action={
            editor ? (
              <button type="button" className="primary" onClick={() => setDialog('new')}>
                <Glyph name="plus" />
                Add item
              </button>
            ) : undefined
          }
        >
          {editor ? 'Add the first thing you buy, then it can be received.' : 'An engineer adds items; then they can be received here.'}
        </EmptyState>
      ) : null}
      {all.length > 0 && shown.length === 0 ? (
        <EmptyState
          icon="stock"
          title={query ? `Nothing matches “${query}”` : 'Nothing in this category'}
          action={
            <button type="button" onClick={() => {
                setQuery('')
                setCategory(null)
              }}>
              Clear the search
            </button>
          }
        />
      ) : null}
      {shown.length > 0 ? (
        layout === 'grid' ? (
          <ItemGrid items={shown} />
        ) : (
          <InventoryTable items={shown} />
        )
      ) : null}

      {dialog === 'new' ? (
        <ItemEditor
          initialName={query}
          onClose={() => setDialog(null)}
          onSaved={(id) => {
            setDialog(null)
            navigate({ name: 'item', id })
          }}
        />
      ) : null}
      {dialog === 'categories' ? <CategoriesDialog onClose={() => setDialog(null)} /> : null}
    </div>
  )
}
